import styled, { css } from 'styled-components';
import { SURFACE_SUBTLE, SURFACE_THUMBNAIL, CHART_GRID_COLOR, TEXT_PRIMARY, TEXT_SECONDARY, TEXT_MUTED } from '../consts';

export const Container = styled.div`
  ${({ theme: { spacings } }) => css`
    padding: ${spacings.s24} 0;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    flex: 1;
    min-height: 0; /* allow the column to constrain its children within the fixed module height */
    overflow: hidden;
    box-sizing: border-box;
  `}
`;

export const ScrollViewport = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

export const CardScrollArea = styled.div`
  display: flex;
  gap: 1rem;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-behavior: smooth;
  flex-grow: 1;
`;

export const ScrollButton = styled.button<{ $side: 'left' | 'right' }>`
  ${({ $side, theme: { colors } }) => css`
    position: absolute;
    top: 50%;
    ${$side}: 0.5rem;
    transform: translateY(-50%);
    z-index: 2;

    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 50%;
    border: 1px solid ${colors.brandDarkTint90};
    background: ${colors.brandLight};
    color: ${TEXT_SECONDARY};
    cursor: pointer;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
    transition: background 0.15s ease;

    &:hover {
      background: ${colors.brandDarkTint95};
    }
  `}
`;

export const ScrollFade = styled.div<{ $side: 'left' | 'right' }>`
  ${({ $side, theme: { colors } }) => css`
    position: absolute;
    top: 0;
    bottom: 0.5rem; /* clear the scroll-area's bottom padding */
    ${$side}: 0;
    width: 3rem;
    z-index: 1;
    pointer-events: none; /* purely visual — never intercepts clicks/scroll */
    background: linear-gradient(to ${$side === 'left' ? 'right' : 'left'}, ${colors.brandLight}, rgba(255, 255, 255, 0));
  `}
`;

export const Card = styled.div`
  ${({ theme: { colors } }) => css`
    background: ${colors.brandLight};
    border: 1px solid ${colors.brandDarkTint90};
    border-radius: 0.75rem;
    box-sizing: border-box;
    width: 18rem;
    height: 100%; /* fill the scroll area's height */
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    padding: 0.375rem;
    gap: 0.5rem;
    overflow: hidden;
  `}
`;

export const HeaderPanel = styled.div`
  background: ${SURFACE_SUBTLE};
  border-radius: 0.5rem;
  padding: 0.75rem;
  flex-shrink: 0; /* keep its natural height; only the metrics list flexes */
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
`;

export const PanelTopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const CardDate = styled.span`
  font-size: 0.8125rem;
  color: ${TEXT_MUTED};
`;

export const CardLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${TEXT_MUTED};
  text-decoration: none;
  flex-shrink: 0;

  &:hover {
    color: ${TEXT_SECONDARY};
  }

  > svg {
    width: 1.143rem;
  }
`;

export const ProfileSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  min-width: 0;
`;

export const Avatar = styled.div`
  ${({ theme: { colors } }) => css`
    position: relative;
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 0.5rem;
    overflow: visible;
    flex-shrink: 0;
    background: ${colors.brandDarkTint90};

    img {
      width: 100%;
      height: 100%;
      border-radius: 0.5rem;
      object-fit: cover;
    }
  `}
`;

export const PlatformBadge = styled.span`
  ${({ theme: { colors } }) => css`
    position: absolute;
    right: -3px;
    bottom: -3px;
    width: 1rem;
    height: 1rem;
    border-radius: 50%;
    background: ${colors.brandLight};
    box-shadow: 0 0 0 1.5px ${colors.brandLight};
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;

    svg,
    img {
      width: 100%;
      height: 100%;
    }
  `}
`;

export const ProfileInfo = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

export const ProfileName = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${TEXT_PRIMARY};
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ProfileHandle = styled.span`
  font-size: 0.75rem;
  color: ${TEXT_MUTED};
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const CaptionRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
`;

export const CaptionText = styled.p`
  font-size: 0.75rem;
  line-height: 1.35;
  color: ${TEXT_SECONDARY};
  margin: 0;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

export const Thumbnail = styled.div`
  position: relative;
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 0.5rem;
  overflow: hidden;
  flex-shrink: 0;
  background: ${SURFACE_THUMBNAIL};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const PlayOverlay = styled.div`
  position: absolute;
  bottom: 0;
  right: 0.357rem;

  svg {
    width: 1rem;
  }
`;

export const MetricsList = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1; /* take the height left after the header */
  min-height: 0; /* let it shrink so its rows can absorb any deficit */
  margin: 0 0.125rem 0 0.125rem;
`;

export const MetricRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center; /* was baseline — centres content as rows resize to fill */
  gap: 0.75rem;
  flex: 1 1 auto; /* rows grow/shrink together to fill the metrics list */
  min-height: 0;
  padding: 0.25rem 0.25rem;
  border-bottom: 1px solid ${CHART_GRID_COLOR};

  &:last-child {
    border-bottom: none;
  }
`;

export const MetricLabel = styled.span`
  font-size: 0.75rem;
  color: ${TEXT_MUTED};
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const MetricValue = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${TEXT_PRIMARY};
  white-space: nowrap;
  text-align: right;
  flex-shrink: 0;
`;

export const EmptyState = styled.p`
  ${({ theme: { colors } }) => css`
    color: ${colors.brandDarkAlpha50};
    text-align: center;
  `}
`;
